/**
 * Filling a class's flashcard sets and notes from what Alter published to S3.
 *
 * classes.js starts every class with sets: [] and notes: [] because Canvas
 * has no opinion about study material -- this is where those arrays get
 * filled in, the same way loadclasses.js fills the sidebar itself from
 * Canvas. Read that file first: the rule here is the same rule for the same
 * reason. This site's job is uploading recordings, and a study-material
 * outage -- the bucket unreachable, a card set that fails to parse, an
 * identity-pool change not yet propagated -- must not cost that.
 *
 * study/ is memos/ with the trust running the other way (see
 * `alter-web/infra/lib/app-stack.ts` and `alter/publish.py`'s module
 * docstring): the browser may only *read* here, at
 * `study/{identityId}/{course code}/{name}`, and only inside its own
 * identity's prefix -- there is no s3:GetObject anywhere else in that
 * policy, least of all under memos/, which runs the same trust the other
 * way (write-only, never read).
 */
import { GetObjectCommand, ListObjectsV2Command, S3Client } from '@aws-sdk/client-s3';

// `alter/publish.py` keys its content type on which Drive folder a file came
// from, not its extension -- but by the time a file is sitting in S3 the
// folder is gone and the extension is what is left, and it was chosen to
// carry the same fact: a card set is JSON, a note is Markdown.
const KIND_BY_EXTENSION = { '.json': 'set', '.md': 'note' };

// study/{identityId}/{course code}/{name}, exactly as `publish_set`'s own
// docstring gives it. The identity segment is matched but not captured:
// every key this module ever sees already came back from a list scoped to
// this browser's own identityId, so re-reading it out would only be
// re-deriving what is already known.
const STUDY_KEY = /^study\/[^/]+\/([^/]+)\/([^/]+)$/;

/**
 * @returns {{course: string, name: string, kind: 'set'|'note'}|null}
 *
 * Pure and total: every string gets an answer. `null` covers everything
 * that is not a readable card set or note in one course's folder -- no
 * course segment, an extension publish.py never writes, or a `memos/` key.
 * The two prefixes are mirror images of each other's trust, and this is the
 * one place that distinction has to be a fact about the string rather than
 * an assumption about where a key came from.
 */
/**
 * One course code reduced to the part that survives being written down.
 *
 * The course segment in a key and the `meta` on a class are the same code
 * with two different normalisations between them, and neither knew about
 * the other:
 *
 *   upload   `courseMetadata` runs `ascii()` over `cls.meta` -- NFKD, the
 *            dash family folded to '-', anything non-ASCII to a space --
 *            and stores that as the object's `course-code`.
 *   filing   `alter/drive.py`'s `folder_name` then replaces whatever
 *            Windows refuses in a folder name, and that folder name is
 *            what `publish_set` writes into the key.
 *   here     the raw `cls.meta` used to be compared against the result of
 *            both, so a Canvas code with an en-dash, a colon or a
 *            non-breaking space in it never matched. The page said "no
 *            card sets for this class" about a set sitting in the bucket.
 *
 * Rather than reimplement either transformation -- two copies of a rule in
 * two languages, free to drift again -- both sides are reduced to letters
 * and digits, which is what every one of those steps preserves.
 *
 * @returns {string} '' when there is nothing to compare on.
 */
export function courseKey(text) {
  return String(text ?? '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function studyKeyParts(key) {
  const match = STUDY_KEY.exec(String(key ?? ''));
  if (!match) return null;
  const [, course, name] = match;
  const dot = name.lastIndexOf('.');
  const kind = dot >= 0 ? KIND_BY_EXTENSION[name.slice(dot)] : undefined;
  return kind ? { course, name, kind } : null;
}

/**
 * The JSON shape `alter/study_file.py`'s CARDS_TASK writes, trusted only as
 * far as having a name and a non-empty card list. A deck that fails to
 * parse is dropped rather than shown broken -- the same choice
 * `parse_cards` makes on the write side, just quieter, because there is no
 * human here to hand a reason to.
 */
function parseSet(text, fallbackName) {
  let raw;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (!raw || !Array.isArray(raw.cards) || raw.cards.length === 0) return null;
  const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name : fallbackName;
  return { name, cards: raw.cards };
}

/**
 * A note has no header to read a title out of -- unlike a card set it is
 * just the Markdown a model wrote -- so the filename is the title, the same
 * way a folder window shows one.
 */
function parseNote(text, filename) {
  return {
    title: filename.replace(/\.md$/i, ''),
    body: text,
    meta: 'From your study notes',
  };
}

/** Every key under a prefix, following continuation tokens rather than
 * trusting the first page -- a course with enough decks and notes in it can
 * pass 1000 keys, ListObjectsV2's own page limit. */
async function listAllKeys(client, bucket, prefix) {
  const keys = [];
  let token;
  do {
    const page = await client.send(
      new ListObjectsV2Command({ Bucket: bucket, Prefix: prefix, ContinuationToken: token }),
    );
    for (const object of page.Contents ?? []) {
      if (object.Key) keys.push(object.Key);
    }
    token = page.IsTruncated ? page.NextContinuationToken : undefined;
  } while (token);
  return keys;
}

/**
 * @returns {{ok: boolean, count: number, message: string}}
 *
 * Never throws -- the same rule loadClasses() follows for Canvas, for the
 * same reason: this site's job is uploading recordings, and that must keep
 * working when study material fails to load.
 *
 * `classes` is mutated in place -- it is `CLASSES` itself, the array
 * screens.js reads directly -- but only a class that actually has matching
 * material in this listing is touched. A class nothing was published for is
 * left exactly as classes.js built it, empty arrays included, rather than
 * being cleared and refilled with nothing. Each touched class's arrays are
 * rebuilt from scratch, not appended to, so calling this twice replaces
 * what a class has instead of doubling it -- the same reason classes.js's
 * replaceClasses clears before it fills.
 */
export async function loadStudy(config, session, classes, deps = {}) {
  const client = deps.client
    ?? new S3Client({ region: config?.region, credentials: session?.credentials });
  // Keyed on `courseKey`, not on `cls.meta` itself, and on the name as well
  // as the code -- see that function for why the two sides of this
  // comparison had drifted apart, and `courseMetadata` for why the name is
  // a legitimate second spelling of the same course.
  const byCourse = new Map();
  for (const cls of classes ?? []) {
    for (const spelling of [cls?.meta, cls?.name]) {
      const key = courseKey(spelling);
      if (key && !byCourse.has(key)) byCourse.set(key, cls);
    }
  }

  let keys;
  try {
    keys = await listAllKeys(client, config?.memoBucket, `study/${session?.identityId}/`);
  } catch (err) {
    // The name, not just a friendly sentence. Every way this can fail --
    // AccessDenied, ExpiredToken, a network drop -- used to arrive here and
    // leave as one indistinguishable message that the caller then did not
    // even show, so a real refusal looked exactly like a class with nothing
    // in it yet (2026-08-13: a set sitting in the bucket, correctly keyed,
    // under the right identity, and the page saying "no card sets").
    const why = err?.name || err?.code || 'unknown error';
    return {
      ok: false,
      count: 0,
      message: `Could not load your study material (${why}).`,
    };
  }

  const setsByClass = new Map();
  const notesByClass = new Map();

  for (const key of keys) {
    const parts = studyKeyParts(key);
    if (!parts) continue; // not a card set or a note -- ignore it rather than guess

    const cls = byCourse.get(courseKey(parts.course));
    if (!cls) continue; // material for a course this identity is not (or no longer) enrolled in

    let text;
    try {
      const object = await client.send(
        new GetObjectCommand({ Bucket: config?.memoBucket, Key: key }),
      );
      text = await object.Body.transformToString();
    } catch {
      continue; // one unreadable object must not sink the rest of the deck
    }

    if (parts.kind === 'set') {
      const set = parseSet(text, parts.name);
      if (!set) continue;
      if (!setsByClass.has(cls)) setsByClass.set(cls, []);
      setsByClass.get(cls).push(set);
    } else {
      if (!notesByClass.has(cls)) notesByClass.set(cls, []);
      notesByClass.get(cls).push(parseNote(text, parts.name));
    }
  }

  let count = 0;
  for (const [cls, sets] of setsByClass) {
    cls.sets.length = 0;
    cls.sets.push(...sets);
    count += sets.length;
  }
  for (const [cls, notes] of notesByClass) {
    cls.notes.length = 0;
    cls.notes.push(...notes);
    count += notes.length;
  }

  return { ok: true, count, message: '' };
}
